import React from "react";
import { Textarea } from "@heroui/react";
import { StepDefinition, FieldDefinition, AttachmentDto } from "../../../../types/types";
import { FileAttachmentHandlers } from "../fields/attachmentsHandler";
import { FieldLabel, renderSingleField, textareaClassNames } from "../dealFormFields";
import { FieldHelpers, FormValue } from "../dealFormTypes";
import { TranslationFn } from "../dealFormFields";

export type NotesStepProps = {
  step: StepDefinition;
  stepId: string;
  formData: Record<string, FormValue>;
  fieldHelpers: FieldHelpers;
  t: TranslationFn;
  selectedFiles: File[];
  setSelectedFiles: React.Dispatch<React.SetStateAction<File[]>>;
  existingAttachments: AttachmentDto[];
  attachmentsToRemove: string[];
  setAttachmentsToRemove: React.Dispatch<React.SetStateAction<string[]>>;
  uploadingFiles: boolean;
  isDragging: boolean;
  setIsDragging: React.Dispatch<React.SetStateAction<boolean>>;
  dragCounter: number;
  setDragCounter: React.Dispatch<React.SetStateAction<number>>;
};

export const NotesStep = ({
  step,
  stepId,
  formData,
  fieldHelpers,
  t,
  selectedFiles,
  setSelectedFiles,
  existingAttachments,
  attachmentsToRemove,
  setAttachmentsToRemove,
  uploadingFiles,
  isDragging,
  setIsDragging,
  dragCounter,
  setDragCounter,
}: NotesStepProps): React.ReactElement | null => {
  const { getFormValue, updateFieldValue } = fieldHelpers;
  const fields = step.fields ?? [];
  const stepData = (formData[stepId] ?? {}) as Record<string, FormValue>;

  const attachments = (
    <FileAttachmentHandlers
      selectedFiles={selectedFiles}
      setSelectedFiles={setSelectedFiles}
      existingAttachments={existingAttachments}
      attachmentsToRemove={attachmentsToRemove}
      setAttachmentsToRemove={setAttachmentsToRemove}
      uploadingFiles={uploadingFiles}
      isDragging={isDragging}
      setIsDragging={setIsDragging}
      dragCounter={dragCounter}
      setDragCounter={setDragCounter}
    />
  );
  const AttachBlock = <div className="col-span-full">{attachments}</div>;

  if (fields.length > 0) {
    const notesFields = fields.map((f: FieldDefinition) =>
      (f.type || "text").toLowerCase() === "textarea"
        ? { ...f, colSpan: 1 }
        : f,
    );
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-5">
        {notesFields.map((f: FieldDefinition) => (
          <React.Fragment key={f.key}>
            {renderSingleField(
              stepId,
              f,
              stepData,
              undefined,
              undefined,
              fieldHelpers,
            )}
          </React.Fragment>
        ))}
        {AttachBlock}
      </div>
    );
  }

  const notesValue = (
    getFormValue(stepId, "notes") ??
    (formData?.[stepId] as Record<string, FormValue> | undefined)?.message ??
    ""
  ) as string;
  return (
    <div className="space-y-5">
      <div>
        <FieldLabel label={step.label || t("additionalMessage")} required />
        <Textarea
          placeholder={step.placeholder || t("additionalMessagePlaceholder")}
          value={notesValue}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            updateFieldValue(
              stepId,
              "notes",
              e.target.value === "" ? undefined : e.target.value,
            )
          }
          variant="bordered"
          minRows={5}
          maxLength={1000}
          classNames={textareaClassNames}
        />
        <p className="text-[11px] text-slate-400 text-right mt-1">
          {String(notesValue ?? "").length} / 1000
        </p>
      </div>

      {attachments}
    </div>
  );
};
